import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import {
  MaterialOptionProps,
  PartyInfoProps,
  PartyOptionProps,
} from "../../types/materialLine";
import { OpenOrderProps } from "../../types/materialOrder";
import {
  MaterialPurchaseDateBy,
  MaterialPurchaseListProps,
  MaterialPurchaseProps,
  MaterialPurchaseSortBy,
  MaterialPurchaseSortOrder,
  MaterialPurchaseState,
} from "../../types/materialPurchase";

export interface MaterialPurchasesState {
  purchases: MaterialPurchaseListProps[];
  totalPurchases: number;
  currentPurchase: MaterialPurchaseProps | null;
  currentPurchaseLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  materials: MaterialOptionProps[];
  materialsLoading: boolean;
  partyInfo: PartyInfoProps[];
  partyInfoRequestId: string | null;
  openOrders: OpenOrderProps[];
  openOrdersLoading: boolean;
  openOrdersRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: MaterialPurchasesState = {
  purchases: [],
  totalPurchases: 0,
  currentPurchase: null,
  currentPurchaseLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  materials: [],
  materialsLoading: false,
  partyInfo: [],
  partyInfoRequestId: null,
  openOrders: [],
  openOrdersLoading: false,
  openOrdersRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchMaterialPurchaseListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  status?: MaterialPurchaseState | null;
  date_by?: MaterialPurchaseDateBy;
  sort_by?: MaterialPurchaseSortBy;
  sort_order?: MaterialPurchaseSortOrder;
};

export const fetchMaterialPurchaseListData = createAsyncThunk(
  "fetchMaterialPurchaseListData",
  async (req: FetchMaterialPurchaseListDataProps) => {
    let response: any;
    await GET(`/v1/admin/material-purchases`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchMaterialPurchaseDetail = createAsyncThunk(
  "fetchMaterialPurchaseDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/material-purchases/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// partyId keeps an edited purchase's own party in the list.
export const fetchMaterialPurchaseParties = createAsyncThunk(
  "fetchMaterialPurchaseParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/material-purchases/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every materials party, active or not, and every party that has purchases.
export const fetchMaterialPurchaseFilterParties = createAsyncThunk(
  "fetchMaterialPurchaseFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/material-purchases/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchMaterialPurchaseMaterials = createAsyncThunk(
  "fetchMaterialPurchaseMaterials",
  async () => {
    let response: any;
    await GET(`/v1/admin/material-purchases/materials`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchMaterialPurchasePartyInfo = createAsyncThunk(
  "fetchMaterialPurchasePartyInfo",
  async (req: { party_id: number; purchase_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/material-purchases/party-info`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The party's open orders, whose lines the picker offers; purchase_id keeps the orders an edited purchase has lines of.
export const fetchMaterialPurchaseOpenOrders = createAsyncThunk(
  "fetchMaterialPurchaseOpenOrders",
  async (req: { party_id: number; purchase_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/material-purchases/orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const materialPurchasesSlice = createSlice({
  name: "materialPurchases",
  initialState,
  reducers: {
    clearCurrentMaterialPurchase: (state) => {
      state.currentPurchase = null;
      state.currentPurchaseLoading = false;
    },
    clearMaterialPurchasePartyData: (state) => {
      state.partyInfo = [];
      state.partyInfoRequestId = null;
      state.openOrders = [];
      state.openOrdersLoading = false;
      state.openOrdersRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMaterialPurchaseListData.pending, (state) => {
      state.purchases = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchMaterialPurchaseListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.purchases = action.payload.data.data.records;
          state.totalPurchases = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.purchases = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchMaterialPurchaseListData.rejected,
      (state, action: any) => {
        state.purchases = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchMaterialPurchaseDetail.pending, (state) => {
      state.currentPurchaseLoading = true;
      state.currentPurchase = null;
    });
    builder.addCase(
      fetchMaterialPurchaseDetail.fulfilled,
      (state, action: any) => {
        state.currentPurchaseLoading = false;
        if (action.payload.data.status === "success") {
          state.currentPurchase = action.payload.data.data;
        }
      }
    );
    builder.addCase(fetchMaterialPurchaseDetail.rejected, (state) => {
      state.currentPurchaseLoading = false;
      state.currentPurchase = null;
    });

    builder.addCase(
      fetchMaterialPurchaseParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.partyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    builder.addCase(
      fetchMaterialPurchaseFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    builder.addCase(fetchMaterialPurchaseMaterials.pending, (state) => {
      state.materialsLoading = true;
    });
    builder.addCase(
      fetchMaterialPurchaseMaterials.fulfilled,
      (state, action: any) => {
        state.materialsLoading = false;
        if (action.payload.data.status === "success") {
          state.materials = action.payload.data.data.records;
        }
      }
    );
    builder.addCase(fetchMaterialPurchaseMaterials.rejected, (state) => {
      state.materialsLoading = false;
    });

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchMaterialPurchasePartyInfo.pending, (state, action) => {
      state.partyInfo = [];
      state.partyInfoRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchMaterialPurchasePartyInfo.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.partyInfoRequestId) return;
        state.partyInfo =
          action.payload.data.status === "success"
            ? action.payload.data.data.records
            : [];
      }
    );

    builder.addCase(
      fetchMaterialPurchaseOpenOrders.pending,
      (state, action) => {
        state.openOrders = [];
        state.openOrdersLoading = true;
        state.openOrdersRequestId = action.meta.requestId;
      }
    );
    builder.addCase(
      fetchMaterialPurchaseOpenOrders.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
        state.openOrders =
          action.payload.data.status === "success"
            ? action.payload.data.data.records
            : [];
      }
    );
    builder.addCase(
      fetchMaterialPurchaseOpenOrders.rejected,
      (state, action) => {
        if (action.meta.requestId !== state.openOrdersRequestId) return;
        state.openOrdersLoading = false;
      }
    );
  },
});

export const {
  clearCurrentMaterialPurchase,
  clearMaterialPurchasePartyData,
} = materialPurchasesSlice.actions;

export const getMaterialPurchases = (state: RootState) =>
  state.materialPurchases.purchases;
export const getTotalMaterialPurchases = (state: RootState) =>
  state.materialPurchases.totalPurchases;
export const getCurrentMaterialPurchase = (state: RootState) =>
  state.materialPurchases.currentPurchase;
export const getCurrentMaterialPurchaseLoading = (state: RootState) =>
  state.materialPurchases.currentPurchaseLoading;
export const getMaterialPurchasePartyOptions = (state: RootState) =>
  state.materialPurchases.partyOptions;
export const getMaterialPurchaseFilterPartyOptions = (state: RootState) =>
  state.materialPurchases.filterPartyOptions;
export const getMaterialPurchaseMaterials = (state: RootState) =>
  state.materialPurchases.materials;
export const getMaterialPurchaseMaterialsLoading = (state: RootState) =>
  state.materialPurchases.materialsLoading;
export const getMaterialPurchasePartyInfo = (state: RootState) =>
  state.materialPurchases.partyInfo;
export const getMaterialPurchaseOpenOrders = (state: RootState) =>
  state.materialPurchases.openOrders;
export const getMaterialPurchaseOpenOrdersLoading = (state: RootState) =>
  state.materialPurchases.openOrdersLoading;

export default materialPurchasesSlice.reducer;
